import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getArticleDetail } from "../lib/api.js";
import type { ArticleDetail, WarehouseStockRef } from "../lib/types.js";
import { Breadcrumbs } from "../ui/Breadcrumbs.js";
import { Badge } from "../ui/Badge.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { PriceDisplay } from "../ui/PriceDisplay.js";
import { StockBadge } from "../ui/StockBadge.js";
import { PartDrawingPreview } from "../ui/PartDrawingPreview.js";
import { formatRelative } from "../lib/format.js";
import { useQuoteCart } from "../contexts/QuoteCartContext.js";
import {
  PackageIcon,
  StoreIcon,
  FactoryIcon,
  CopyIcon,
  CartIcon,
  DownloadIcon,
  HashIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  InfoIcon,
  AlertIcon,
  DocumentIcon,
} from "../lib/icons.js";
import { pushRecent } from "./HomePage.js";

/**
 * Obrazovka F — detail dílu.
 *
 * Layout:
 *  - Levý blok: obrázek
 *  - Pravý blok: název, article number, metadata, cena, dvojitý sklad, akce
 *  - Pod hlavičkou: kompatibilní modely (grouped by brand+series)
 */
export function PartDetailPage(): JSX.Element {
  const { articleNumber } = useParams<{ articleNumber: string }>();
  const [data, setData] = useState<ArticleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!articleNumber) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getArticleDetail(articleNumber)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        pushRecent({
          url: `/part/${encodeURIComponent(res.article.articleNumber)}`,
          label: res.article.name ?? res.article.articleNumber,
          subtitle: res.article.articleNumber,
        });
      })
      .catch((err) => !cancelled && setError(String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [articleNumber]);

  if (loading) return <LoadingBlock label="Načítám detail dílu…" />;
  if (error)
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {error}
        </div>
      </div>
    );
  if (!data)
    return (
      <EmptyState
        title="Díl nenalezen"
        description="Ověřte číslo dílu nebo se vraťte na vyhledávání."
      />
    );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-5">
        <Breadcrumbs
          items={[
            { label: "Díly", to: "/search/part" },
            { label: data.article.articleNumber },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-8">
        <DrawingShowcase
          drawings={data.drawings}
          fallbackImage={data.article.imageUrl}
          fallbackAlt={data.article.name}
        />
        <ProductInfo article={data} />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CompatibilitySection article={data} />
        </div>
        <div>
          <ActionSidebar article={data} />
        </div>
      </div>

      {/* PIM média a dokumenty — schovají se pokud nic není */}
      <PimGallerySection article={data} />
      <PimDocumentsSection article={data} />
    </div>
  );
}

/* ───────────────────── PIM galerie ───────────────────── */

function PimGallerySection({ article }: { article: ArticleDetail }): JSX.Element | null {
  const media = article.article.pimMedia ?? [];
  if (media.length === 0) return null;

  return (
    <section className="mt-10 bg-white rounded-2xl border border-zinc-200 shadow-card overflow-hidden">
      <header className="px-5 py-4 border-b border-zinc-100">
        <h2 className="text-base font-semibold text-zinc-900">Obrazová galerie</h2>
        <p className="text-xs text-zinc-500 mt-0.5">
          {media.length}{" "}
          {media.length === 1 ? "obrázek" : media.length < 5 ? "obrázky" : "obrázků"}{" "}
          z PIM
        </p>
      </header>
      <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {media.map((m, idx) => (
          <a
            key={idx}
            href={m.url}
            target="_blank"
            rel="noopener noreferrer"
            className="aspect-square bg-zinc-50 rounded-lg overflow-hidden border border-zinc-200 hover:border-ktm-orange transition-colors group"
            title={`${m.purpose} · ${m.mediaType}`}
          >
            <img
              src={m.url}
              alt={m.purpose}
              className="w-full h-full object-contain p-2 group-hover:scale-[1.03] transition-transform"
            />
          </a>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────── PIM dokumenty (PDF) ───────────────────── */

interface PimDocItem {
  id: string;
  url: string | null;
  documentType: string;
  category: string;
  language: string | null;
  version: string | null;
  sizeBytes: number | null;
}

function PimDocumentsSection({ article }: { article: ArticleDetail }): JSX.Element | null {
  const docs: PimDocItem[] = (article.article.pimDocuments ?? []).filter(
    (d): d is PimDocItem => d.url !== null,
  );
  if (docs.length === 0) return null;

  // Seskupení per documentType
  const byType = new Map<string, PimDocItem[]>();
  for (const d of docs) {
    const existing = byType.get(d.documentType);
    if (existing) existing.push(d);
    else byType.set(d.documentType, [d]);
  }

  return (
    <section className="mt-8 bg-white rounded-2xl border border-zinc-200 shadow-card overflow-hidden">
      <header className="px-5 py-4 border-b border-zinc-100">
        <h2 className="text-base font-semibold text-zinc-900">
          Dokumenty ke stažení
        </h2>
        <p className="text-xs text-zinc-500 mt-0.5">
          {docs.length} PDF (návody, homologace, ES prohlášení)
        </p>
      </header>
      <div className="p-5 space-y-4">
        {Array.from(byType.entries()).map(([type, items]) => (
          <div key={type}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-600">
              {docTypeLabel(type)}{" "}
              <span className="font-normal text-zinc-400">{items.length}</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {items.map((d) => (
                <a
                  key={d.id}
                  href={d.url!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg border border-zinc-200 hover:border-ktm-orange hover:shadow-sm transition-all group"
                >
                  <div className="w-10 h-10 rounded bg-zinc-100 flex items-center justify-center text-zinc-500 group-hover:text-ktm-orange group-hover:bg-orange-50 transition-colors shrink-0">
                    <DocumentIcon size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-zinc-900 truncate">
                      {docTypeLabel(d.documentType)}
                      {d.version && (
                        <span className="text-xs text-zinc-400 ml-1">{d.version}</span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 flex items-center gap-2">
                      {d.language && <span>{d.language}</span>}
                      {d.sizeBytes != null && (
                        <>
                          {d.language && <span className="text-zinc-300">·</span>}
                          <span>{formatBytesLocal(d.sizeBytes)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function docTypeLabel(t: string): string {
  return (
    {
      BED: "Návod k použití",
      FACTSHEET: "Factsheet",
      GEN: "Obecný dokument",
      GENABE: "Technický průkaz (ABE)",
      GENEC: "ES prohlášení o shodě",
      GENECE: "Homologace ECE",
      GENECV: "Homologace ECV",
      GENKAT: "Katalog",
      PDF: "PDF dokument",
      CAT: "Katalog",
      HELMSIZE: "Velikostní tabulka přilby",
      PROTECTMAT: "Ochranné materiály",
      PROD: "Produktový list",
      SL: "Specifikace",
    }[t] ?? t
  );
}

function formatBytesLocal(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} kB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

/* ───────────────────── Výkres s vyznačeným dílem ───────────────────── */

function DrawingShowcase({
  drawings,
  fallbackImage,
  fallbackAlt,
}: {
  drawings: import("../lib/types.js").ArticleDrawing[];
  fallbackImage: string | null;
  fallbackAlt: string | null;
}): JSX.Element {
  const navigate = useNavigate();
  const [activeIdx, setActiveIdx] = useState(0);

  // Pokud nejsou výkresy, zobraz fallback obrázek (nebo placeholder)
  if (drawings.length === 0) {
    if (fallbackImage) {
      return (
        <div className="aspect-square bg-white rounded-2xl border border-zinc-200 shadow-card flex items-center justify-center overflow-hidden">
          <img
            src={fallbackImage}
            alt={fallbackAlt ?? ""}
            className="w-full h-full object-contain p-8"
          />
        </div>
      );
    }
    return (
      <div className="aspect-square bg-white rounded-2xl border border-zinc-200 shadow-card flex items-center justify-center text-zinc-300 text-sm">
        Výkres není k dispozici
      </div>
    );
  }

  const active = drawings[activeIdx] ?? drawings[0]!;
  const hasMultiple = drawings.length > 1;

  return (
    <div className="space-y-3">
      <div className="relative">
        <PartDrawingPreview
          drawing={active}
          onOpenFull={() =>
            navigate(`/model/${active.modelId}/drawing/${active.subgroupId}`)
          }
        />
        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={() =>
                setActiveIdx((i) => (i - 1 + drawings.length) % drawings.length)
              }
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/95 border border-zinc-200 shadow-md flex items-center justify-center text-zinc-700 hover:text-ktm-orange hover:border-ktm-orange-100 transition-colors"
              aria-label="Předchozí výkres"
            >
              <ChevronLeftIcon size={18} />
            </button>
            <button
              type="button"
              onClick={() => setActiveIdx((i) => (i + 1) % drawings.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/95 border border-zinc-200 shadow-md flex items-center justify-center text-zinc-700 hover:text-ktm-orange hover:border-ktm-orange-100 transition-colors"
              aria-label="Další výkres"
            >
              <ChevronRightIcon size={18} />
            </button>
          </>
        )}
      </div>

      <div className="flex items-start justify-between gap-3 px-1">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium flex items-center gap-2">
            Pozice
            {active.lineNumber != null && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-ktm-orange text-white text-[10px] font-semibold">
                {active.lineNumber}
              </span>
            )}
            na výkresu
          </div>
          <div className="text-sm font-medium text-zinc-900 mt-0.5 truncate">
            {active.subgroupDescription}
            <span className="ml-1.5 text-zinc-400 font-mono text-xs">
              {active.subgroupCode}
            </span>
          </div>
          {active.modelDescription && (
            <div className="text-xs text-zinc-500 mt-0.5 truncate">
              {active.modelDescription}
            </div>
          )}
        </div>
        {hasMultiple && (
          <span className="text-[11px] text-zinc-500 whitespace-nowrap pt-1">
            {activeIdx + 1} / {drawings.length}
          </span>
        )}
      </div>
    </div>
  );
}

/* ───────────────────── Info (cena + sklady) ───────────────────── */

function ProductInfo({ article }: { article: ArticleDetail }): JSX.Element {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  async function copy(value: string, field: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      // ignore
    }
  }

  const isDiscontinued = article.articleStatus === "80";
  const hasReplacement = article.replacement.replacedBy.length > 0;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Badge tone="orange">
          {article.article.vendor ?? "KTM"}
        </Badge>
        {article.article.productHierarchyL2 && (
          <Badge tone="blue">{article.article.productHierarchyL2}</Badge>
        )}
        {article.article.productType && (
          <Badge>{article.article.productType}</Badge>
        )}
        {isDiscontinued && (
          <Badge tone="red">Zrušený díl</Badge>
        )}
        {hasReplacement && !isDiscontinued && (
          <Badge tone="amber">Nahrazeno</Badge>
        )}
      </div>

      <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 text-balance">
        {article.article.name ?? "Bez názvu"}
      </h1>

      <div className="mt-2 flex items-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={() => copy(article.article.articleNumber, "an")}
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-ktm-orange transition-colors"
          title="Kopírovat číslo dílu"
        >
          <HashIcon size={16} />
          <span className="font-mono text-sm">
            {article.article.articleNumber}
          </span>
          <CopyIcon size={14} />
          {copiedField === "an" && <span className="text-xs text-emerald-600">Zkopírováno</span>}
        </button>
        {article.article.gtin && (
          <button
            type="button"
            onClick={() => copy(article.article.gtin!, "gtin")}
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-ktm-orange transition-colors"
            title="Kopírovat GTIN / EAN"
          >
            <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">EAN</span>
            <span className="font-mono text-sm">{article.article.gtin}</span>
            <CopyIcon size={14} />
            {copiedField === "gtin" && <span className="text-xs text-emerald-600">Zkopírováno</span>}
          </button>
        )}
      </div>

      {article.article.description && (
        /* Description je HTML (AI-generated). Renderujeme přes
           dangerouslySetInnerHTML protože zdroj je pod naší kontrolou
           (MiniMax AI + naše prompty). */
        <div
          className="mt-4 text-zinc-700 leading-relaxed prose prose-sm max-w-none prose-headings:font-semibold prose-p:my-2 prose-ul:my-2"
          dangerouslySetInnerHTML={{ __html: article.article.description }}
        />
      )}
      {article.article.features && (
        <div
          className="mt-3 text-zinc-700 leading-relaxed prose prose-sm max-w-none prose-ul:my-2 prose-li:my-0.5"
          dangerouslySetInnerHTML={{ __html: article.article.features }}
        />
      )}

      {/* Cena + sklady */}
      <div className="mt-6 p-5 rounded-2xl border border-zinc-200 bg-white shadow-card">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 pb-4 border-b border-zinc-100">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium mb-2">
              Cena
            </div>
            {article.price ? (
              <PriceDisplay price={article.price} size="lg" />
            ) : (
              <div className="text-zinc-400 text-sm">Cena nenastavena</div>
            )}
            {article.price?.source && (
              <div className="mt-2 text-[11px] text-zinc-400">
                Zdroj: {article.price.source}
              </div>
            )}
          </div>
          <PhysicalStockHero warehouses={article.warehouses} />
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <StockCard
            icon={<StoreIcon size={16} />}
            label="KTM Vlašim (dealer sklad dle KTM BFF)"
            qty={article.stock.dealer.qty}
            availability={article.stock.dealer.availability}
            updatedAt={article.stock.dealer.updatedAt ?? null}
            tone="dealer"
          />
          <StockCard
            icon={<FactoryIcon size={16} />}
            label="KTM AG (výrobce)"
            qty={article.stock.manufacturer.qty}
            availability={article.stock.manufacturer.availability}
            updatedAt={article.stock.manufacturer.updatedAt ?? null}
            etaDate={article.stock.manufacturer.etaDate ?? null}
            tone="manufacturer"
          />
        </div>
      </div>

      {/* Hierarchy breadcrumb */}
      {article.article.productHierarchy && (
        <div className="mt-5 flex items-center gap-2 text-xs text-zinc-500 flex-wrap">
          <span className="uppercase tracking-wider font-medium">Zařazení:</span>
          <span className="text-zinc-700">{article.article.productHierarchy}</span>
          {article.article.purposeOfUse && (
            <Badge tone="neutral">{article.article.purposeOfUse}</Badge>
          )}
        </div>
      )}

      {/* Replacement warning — tento díl byl nahrazen */}
      {article.replacement.replacedBy.length > 0 && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0 mt-0.5">
              <AlertIcon size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-amber-900">
                Tento díl byl nahrazen
              </div>
              <ul className="mt-2 space-y-1.5">
                {article.replacement.replacedBy.map((r, idx) => (
                  <li key={`${r.articleNumber}-${idx}`} className="flex items-center gap-2 flex-wrap text-sm">
                    <Link
                      to={`/part/${encodeURIComponent(r.articleNumber)}`}
                      className="font-mono font-medium text-amber-900 underline underline-offset-2 hover:text-ktm-orange"
                    >
                      {r.articleNumber}
                    </Link>
                    {r.name && <span className="text-zinc-700">{r.name}</span>}
                    <Badge tone="amber">
                      {r.conversionType} {r.relationType ? `· ${r.relationType}` : ""}
                      {parseFloat(r.unitQuantity) !== 1 && ` · ${r.unitQuantity}×`}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Replaces older — info panel */}
      {article.replacement.replaces.length > 0 && (
        <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-md bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0 mt-0.5">
              <InfoIcon size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sky-900">
                Tento díl nahrazuje starší varianty
              </div>
              <ul className="mt-2 space-y-1.5">
                {article.replacement.replaces.slice(0, 5).map((r, idx) => (
                  <li key={`${r.articleNumber}-${idx}`} className="flex items-center gap-2 flex-wrap text-sm">
                    <Link
                      to={`/part/${encodeURIComponent(r.articleNumber)}`}
                      className="font-mono text-sky-900 underline underline-offset-2 hover:text-ktm-orange"
                    >
                      {r.articleNumber}
                    </Link>
                    {r.name && <span className="text-zinc-600">{r.name}</span>}
                  </li>
                ))}
                {article.replacement.replaces.length > 5 && (
                  <li className="text-xs text-sky-700">
                    … a dalších {article.replacement.replaces.length - 5}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Metadata */}
      <dl className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-3xl">
        <MetaItem label="Váha">
          {article.article.weightGrams
            ? `${article.article.weightGrams} g`
            : "—"}
        </MetaItem>
        <MetaItem label="Balení">
          {article.article.packageSize && article.article.measureUnit
            ? `${article.article.packageSize} ${article.article.measureUnit}`
            : "—"}
        </MetaItem>
        <MetaItem label="Kompatibilních modelů">
          <span className="font-semibold text-zinc-900">
            {article.compatibleCount}
          </span>
        </MetaItem>
        <MetaItem label="DPH sazba">
          {article.price ? `${article.price.vatRate} %` : "—"}
        </MetaItem>
        {article.price?.discountGroup && (
          <MetaItem label="Slevová skupina">
            <span className="font-mono text-sm">{article.price.discountGroup}</span>
          </MetaItem>
        )}
        {article.price?.validFrom && (
          <MetaItem label="Cena platí od">
            {new Date(article.price.validFrom).toLocaleDateString("cs-CZ")}
          </MetaItem>
        )}
        {article.salesOrg && (
          <MetaItem label="SalesOrg">
            <span className="font-mono text-sm">{article.salesOrg}</span>
          </MetaItem>
        )}
        {article.article.ftpSyncedAt && (
          <MetaItem label="Synced z FTP">
            {new Date(article.article.ftpSyncedAt).toLocaleDateString("cs-CZ")}
          </MetaItem>
        )}
      </dl>
    </div>
  );
}

function MetaItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-zinc-700">{children}</dd>
    </div>
  );
}

/**
 * Hlavní blok skladovosti ve Vlašimi (fyzický sklad = source of truth).
 * Zobrazuje `qtySellable = available + inbound` jako hlavní číslo.
 */
function PhysicalStockHero({
  warehouses,
}: {
  warehouses: WarehouseStockRef[];
}): JSX.Element {
  const own = warehouses.find((w) => w.kind === "own");
  if (!own) {
    return (
      <div className="flex items-center gap-3 text-zinc-400 text-sm">
        <PackageIcon size={18} />
        <span>Není ve fyzickém skladu</span>
      </div>
    );
  }

  const sellable = own.qtySellable;
  const inStock = own.qtyAvailable;
  const inbound = own.qtyInbound;
  const hasStock = sellable > 0;

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium mb-2 flex items-center gap-1.5">
        <PackageIcon size={12} />
        {own.name} · fyzický sklad
      </div>
      <div className="flex items-baseline gap-3">
        <div
          className={`text-3xl font-semibold leading-none ${
            hasStock ? "text-emerald-700" : "text-zinc-400"
          }`}
        >
          {sellable} ks
        </div>
        <div className="text-xs text-zinc-500">
          {hasStock ? "lze prodat" : "není skladem"}
        </div>
      </div>
      {hasStock && (inbound > 0 || own.qtyOnHand !== inStock) && (
        <div className="mt-1.5 text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
          {inStock > 0 && (
            <span>
              <span className="font-medium text-zinc-700">{inStock}</span>{" "}
              dostupných
            </span>
          )}
          {inbound > 0 && (
            <>
              <span className="text-zinc-300">·</span>
              <span>
                <span className="font-medium text-amber-700">+{inbound}</span>{" "}
                v příjmu
              </span>
            </>
          )}
          {own.qtyOnHand - own.qtyAvailable > 0 && (
            <>
              <span className="text-zinc-300">·</span>
              <span className="text-zinc-400">
                {own.qtyOnHand - own.qtyAvailable} rezervováno
              </span>
            </>
          )}
        </div>
      )}
      <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-400">
        <span>Synced {formatRelative(own.syncedAt)}</span>
        {own.matchStatus === "via_replacement" && own.resolvedVia && (
          <span className="text-amber-700" title={`Propojeno přes staré číslo ${own.resolvedVia}`}>
            · via {own.resolvedVia}
          </span>
        )}
      </div>
    </div>
  );
}

function StockCard({
  icon,
  label,
  qty,
  availability,
  updatedAt,
  etaDate,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  qty: number | null;
  availability: ArticleDetail["stock"]["dealer"]["availability"];
  updatedAt: string | null;
  etaDate?: string | null;
  tone: "dealer" | "manufacturer";
}): JSX.Element {
  const borderColor =
    tone === "dealer" ? "border-emerald-100" : "border-sky-100";
  const iconBg =
    tone === "dealer"
      ? "bg-emerald-50 text-emerald-700"
      : "bg-sky-50 text-sky-700";

  const showEta =
    etaDate &&
    (availability === "notAvailable" || availability === "unknown" || (qty ?? 0) === 0);

  return (
    <div className={`rounded-xl border ${borderColor} bg-white/50 p-3`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-md ${iconBg} flex items-center justify-center`}>
          {icon}
        </div>
        <div className="text-xs font-medium text-zinc-700">{label}</div>
      </div>
      <StockBadge
        label=""
        stock={{ qty, availability, updatedAt }}
        emphasis="md"
      />
      {showEta && (
        <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-50 text-amber-800 px-2 py-0.5 text-[11px] font-medium">
          Přijde {new Date(etaDate!).toLocaleDateString("cs-CZ")}
        </div>
      )}
      <div className="mt-2 text-[11px] text-zinc-400">
        Aktualizováno {formatRelative(updatedAt)}
      </div>
    </div>
  );
}

/* ───────────────────── Kompatibilní modely ───────────────────── */

function CompatibilitySection({ article }: { article: ArticleDetail }): JSX.Element {
  const [filter, setFilter] = useState("");
  const hasBffCompat = article.compatibleCount > 0;
  const hasPimCompat = (article.pimCompatibleCount ?? 0) > 0;

  const grouped = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const filtered = article.compatibleModels.filter(
      (m) =>
        !f ||
        m.description?.toLowerCase().includes(f) ||
        m.articleNumber?.toLowerCase().includes(f) ||
        String(m.year ?? "").includes(f),
    );
    // Group by series (prefix z description, první 2 slova)
    const g = new Map<string, typeof filtered>();
    for (const m of filtered) {
      const series = (m.description ?? "Jiné").split(/\s+/).slice(0, 2).join(" ");
      const arr = g.get(series) ?? [];
      arr.push(m);
      g.set(series, arr);
    }
    return Array.from(g.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [article.compatibleModels, filter]);

  // PIM kompatibilita — seskupená per "první 2 slova" description
  const pimGrouped = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const pim = article.pimCompatibleModels ?? [];
    const filtered = pim.filter(
      (m) =>
        !f ||
        m.description.toLowerCase().includes(f) ||
        String(m.year ?? "").includes(f),
    );
    const g = new Map<string, typeof filtered>();
    for (const m of filtered) {
      const series = m.description.split(/\s+/).slice(0, 2).join(" ");
      const arr = g.get(series) ?? [];
      arr.push(m);
      g.set(series, arr);
    }
    return Array.from(g.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [article.pimCompatibleModels, filter]);

  return (
    <section className="bg-white rounded-2xl border border-zinc-200 shadow-card overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-zinc-900">
            Kompatibilní modely
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {hasBffCompat && (
              <span>{article.compatibleCount} z náhradních dílů</span>
            )}
            {hasBffCompat && hasPimCompat && <span className="mx-1.5">·</span>}
            {hasPimCompat && (
              <span>{article.pimCompatibleCount} z PIM (ZBH2BIKE)</span>
            )}
            {!hasBffCompat && !hasPimCompat && "Žádná kompatibilita"}
          </p>
        </div>
        {(article.compatibleModels.length + (article.pimCompatibleModels?.length ?? 0)) > 10 && (
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtr…"
            className="h-8 px-3 rounded-md border border-zinc-200 text-sm focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none w-48"
          />
        )}
      </div>
      <div className="max-h-[500px] overflow-auto">
        {grouped.length === 0 && pimGrouped.length === 0 ? (
          <EmptyState
            icon={<PackageIcon size={24} />}
            title="Žádná shoda"
            description="Upravte filtr."
          />
        ) : (
          <>
            {grouped.map(([series, models]) => (
              <div key={series}>
                <div className="sticky top-0 bg-zinc-50/95 backdrop-blur px-5 py-2 border-b border-zinc-100 text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                  {series}{" "}
                  <span className="font-normal text-zinc-400">· {models.length}</span>
                </div>
                <ul className="divide-y divide-zinc-100">
                  {models.map((m) => (
                    <li key={m.id}>
                      <Link
                        to={`/model/${m.id}`}
                        className="group flex items-center gap-3 px-5 py-3 hover:bg-zinc-50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-zinc-900 group-hover:text-ktm-orange transition-colors truncate">
                            {m.description}
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
                            <span>{m.brand.shortName}</span>
                            {m.year && (
                              <>
                                <span className="text-zinc-300">·</span>
                                <span>{m.year}</span>
                              </>
                            )}
                            {m.articleNumber && (
                              <>
                                <span className="text-zinc-300">·</span>
                                <span className="font-mono">{m.articleNumber}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <ChevronRightIcon
                          size={14}
                          className="text-zinc-300 group-hover:text-ktm-orange group-hover:translate-x-0.5 transition-all"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* PIM kompatibilita (ZBH2BIKE) — text-level, neprolinkované na BFF Model */}
            {pimGrouped.map(([series, models]) => (
              <div key={`pim-${series}`}>
                <div className="sticky top-0 bg-amber-50/95 backdrop-blur px-5 py-2 border-b border-amber-100 text-xs font-semibold text-amber-800 uppercase tracking-wide flex items-center gap-2">
                  <span>{series}</span>
                  <span className="font-normal text-amber-600">· {models.length}</span>
                  <span className="ml-auto text-[10px] font-normal text-amber-600 normal-case">
                    z PIM
                  </span>
                </div>
                <ul className="divide-y divide-zinc-100">
                  {models.map((m, idx) => (
                    <li key={`${series}-${idx}`}>
                      {m.modelId ? (
                        <Link
                          to={`/model/${m.modelId}`}
                          className="group flex items-center gap-3 px-5 py-3 hover:bg-zinc-50 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-zinc-900 group-hover:text-ktm-orange transition-colors truncate">
                              {m.description}
                            </div>
                            {m.year && (
                              <div className="text-xs text-zinc-500 mt-0.5">{m.year}</div>
                            )}
                          </div>
                          <ChevronRightIcon
                            size={14}
                            className="text-zinc-300 group-hover:text-ktm-orange transition-colors"
                          />
                        </Link>
                      ) : (
                        <div className="flex items-center gap-3 px-5 py-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-zinc-900 truncate">
                              {m.description}
                            </div>
                            {m.year && (
                              <div className="text-xs text-zinc-500 mt-0.5">{m.year}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );
}

/* ───────────────────── Akce ───────────────────── */

function ActionSidebar({ article }: { article: ArticleDetail }): JSX.Element {
  const { addItem } = useQuoteCart();
  const hasPrice = !!article.price;
  const canAddToQuote = hasPrice;

  function handleAddToQuote(): void {
    if (!article.price) return;
    addItem({
      articleNumber: article.article.articleNumber,
      articleName: article.article.name,
      unitPriceNetCzk: article.price.net.czk,
      vatRate: article.price.vatRate,
      articleImageUrl: article.article.imageUrl,
    });
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-card p-5 space-y-3 lg:sticky lg:top-24">
      <Button
        fullWidth
        iconLeft={<CartIcon size={18} />}
        onClick={handleAddToQuote}
        disabled={!canAddToQuote}
      >
        Přidat do nabídky
      </Button>
      {!hasPrice && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
          <InfoIcon size={14} className="mt-0.5 flex-shrink-0" />
          <span>Bez ceny nelze přidat. Nejprve nastavte cenu přes CSV import.</span>
        </div>
      )}
      <Button
        fullWidth
        variant="secondary"
        iconLeft={<DownloadIcon size={16} />}
        disabled
      >
        Export do PDF z detailu
      </Button>

      <div className="pt-3 mt-3 border-t border-zinc-100">
        <div className="flex items-start gap-2 text-xs text-zinc-500">
          <InfoIcon size={14} className="mt-0.5 flex-shrink-0" />
          <p>
            Přidat do nabídky můžete i přímo z výkresu — tam se uloží i rozkres
            pro PDF.
          </p>
        </div>
      </div>

      {article.article.shopify && (
        <div className="pt-3 mt-3 border-t border-zinc-100">
          <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium mb-2">
            Shopify
          </div>
          <div className="text-xs text-zinc-600 space-y-1">
            <div>
              Status:{" "}
              <Badge
                tone={
                  article.article.shopify.status === "published"
                    ? "green"
                    : "neutral"
                }
              >
                {article.article.shopify.status ?? "—"}
              </Badge>
            </div>
            {article.article.shopify.handle && (
              <div className="font-mono truncate">
                {article.article.shopify.handle}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
